import { Suspense, lazy, useEffect, useRef } from 'react';
import { isLinkableTin } from '../../lib/tin';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router';
import { LocaleLink as Link } from '../../i18n/locale-link';
import { trackEvent } from '../../lib/events-client';
import { track } from '../../lib/funnel';
import { Seo } from '../../components/seo';
import { countryConfig } from '../../country';
import { buildTenderSeoTitle } from '../../lib/tender-seo-title';
import { fetchTender, fetchFullSummary, unlockTender } from '../../lib/catalog-client';
import { sourceTenderUrl } from '../../lib/source-links';
import { tenderPath, extractTenderId } from '../../lib/slug';
import { useAuthOrigin } from '../../lib/auth-origin';
import { plansPathWithContext } from '../../lib/pending-checkout';
import { isClosedWithoutOutcome, isTenderIndexable } from '../../lib/tender-status';
import { localePath } from '../../i18n/locale-path';
import { apiBaseUrl } from '../../lib/api';
import {
  shouldRetryTenderQuery,
  tenderErrorKind,
  tenderErrorReason,
  tenderRetryDelay,
} from '../../lib/tender-error';
import { formatObligationTerm } from '../../lib/obligation-term';
import {
  lotDetailRows,
  lotPositions,
  positionLine,
  procedureRowDuplicatesLotPlace,
} from '../../lib/lot-details';
import { lotIdentityParts } from '../../lib/lot-identity';
import { useAuth } from '../../lib/auth-context';
import { usePageTitle } from '../../lib/page-title';
import { useHydrated } from '../../lib/use-hydrated';
import { useLocale, useT } from '../../i18n/locale-context';
import {
  daysLeft,
  daysWord,
  formatBudget,
  formatBudgetCompact,
  formatDeadline,
  formatPhone,
  lotsWord,
  statusKind,
} from '../../lib/format';
import { buildTenderFacts } from '../../lib/tender-facts';
import { ArrowRight, DownloadIcon, PhoneIcon } from '../../components/icons';
import { BidWaitlistButton } from '../../components/bid-waitlist-button';
import { SaveStar } from '../../components/save-star';
import { BID_WAITLIST_VISIBLE } from '../../lib/bid-waitlist-switch';
import { AnalyticsTeaser } from '../../components/analytics-teaser';
import { InfoHint } from '../../components/info-hint';
import { TenderGateCard, type TenderGateState } from '../../components/tender-gate-card';
import { ContactRevealCard } from '../../components/contact-reveal-card';
import {
  CompanyDataLink,
  WinnerContactCappedNote,
  WinnerContactLines,
} from '../../components/winner-contact';
import { OutcomeWall } from '../../components/outcome-wall';
import { lazyRoute } from '../../lib/lazy-route';
import { useOnline } from '../../lib/online-status';
import { BaseRatesPanel } from '../../components/base-rates-panel';
import { BuyerGraphPanel } from '../../components/buyer-graph-panel';
import { SimilarWinnersPanel } from '../../components/similar-winners-panel';
import { TenderIntegrityPanel } from '../../components/tender-integrity-panel';
import { SimilarTenders } from '../../components/similar-tenders';
import {
  ClosedLotNotice,
  SimilarLiveLots,
  useSimilarLiveShown,
} from '../../components/closed-lot-notice';
import { TenderOverview } from '../../components/tender-overview';
import { LotNumberChip } from '../../components/lot-number-chip';
import { BuyerTrustStrip } from '../../components/buyer-trust-strip';
import { ChannelSubscribeCta } from '../../components/channel-subscribe-cta';
import { MobileSectionNav } from '../../components/mobile-section-nav';

/**
 * «Пакет заявки» — a SEPARATE CHUNK, not part of the tender page bundle.
 *
 * The panel and its client are ~1 300 lines that only a hand-picked cohort can
 * ever render, and `/tender/*` is the most-loaded route on the site (103 652
 * views in 30 days to 2026-09-10). A static import put all of it — the DOCX
 * copy, the questionnaire, the three-colour checklist — into the bundle every
 * visitor downloads to read a lot they cannot build a package for. Lazy keeps
 * the feature literally absent for everybody outside the beta, which is the
 * contract («на текущее НИКАК не влияет»), not just invisible.
 *
 * `lazyRoute` is the same wrapper the code-split routes use: a deploy renames
 * every chunk and the newest three releases are kept, so a tab opened before a
 * deploy would otherwise hit a 404 on this import and show an error screen.
 *
 * Under SSR/prerender it is never rendered at all — `data.bidPackage` is absent
 * for the anonymous crawler — so the prerendered HTML is byte-identical to what
 * it was before the feature existed and the `Suspense` boundary never suspends.
 */
const BidPackagePanel = lazy(
  lazyRoute('bid-package', () =>
    import('../../components/bid-package-panel').then((m) => ({ default: m.BidPackagePanel })),
  ),
);

type TFn = (key: string, params?: Record<string, string | number>) => string;

/**
 * Kazakhstan's requirements block says only what is true (owner, 2026-10-21):
 * no sentence on a lot that carries no file. The API's half — «Разбираем
 * документы» only before the text is extracted — is `strictPending` in
 * `requirements-state.ts`. Uzbekistan keeps its block as it is.
 */
const KZ_QUIET_REQUIREMENTS = countryConfig.code !== 'uz'; // KZ, and KG like KZ

/**
 * Kazakhstan's lot header carries the announcement number, copied with a tap
 * (owner, 2026-10-21: lots are found by their platform number). Uzbekistan's
 * header stays as it is — see the note where the chip renders.
 */
const KZ_NUMBER_IN_HEADER = countryConfig.code !== 'uz'; // KZ, and KG like KZ

/**
 * Loading skeleton that mirrors the real detail layout block-for-block — the
 * status/title/tags header, the budget + deadline grid, the AI-summary block,
 * and the gated contact card. Same containers, paddings and dividers as the
 * loaded page, so when the data arrives nothing jumps (no CLS / pop-in). The
 * conditional source/procedure/documents sections aren't reserved — they append
 * below and never displace what's already painted.
 */
function TenderDetailSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm">
        {/* Header: status, title, tags */}
        <div className="border-b border-edge-light p-5 sm:p-6">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-[7px] w-[7px] rounded-full bg-edge" />
            <span className="block h-[20px] w-20 animate-pulse rounded-pill bg-edge" />
          </div>
          <div className="mb-3 flex flex-col gap-2">
            <span className="block h-[24px] w-[90%] animate-pulse rounded bg-edge" />
            <span className="block h-[24px] w-[62%] animate-pulse rounded bg-edge" />
          </div>
          <div className="flex flex-wrap gap-[5px]">
            <span className="block h-[19px] w-24 animate-pulse rounded-pill bg-edge" />
            <span className="block h-[19px] w-20 animate-pulse rounded-pill bg-edge" />
          </div>
        </div>

        {/* Budget + deadline */}
        <div className="grid grid-cols-1 divide-y divide-edge-light border-b border-edge-light sm:grid-cols-2 sm:divide-x sm:divide-y-0">
          <div className="p-5 sm:p-6">
            <span className="mb-2 block h-[11px] w-16 animate-pulse rounded bg-edge" />
            <span className="block h-[22px] w-32 animate-pulse rounded bg-edge" />
          </div>
          <div className="p-5 sm:p-6">
            <span className="mb-2 block h-[11px] w-16 animate-pulse rounded bg-edge" />
            <span className="block h-[20px] w-28 animate-pulse rounded bg-edge" />
          </div>
        </div>

        {/* AI summary */}
        <div className="p-5 sm:p-6">
          <div className="mb-3 flex items-center gap-2">
            <span className="block h-[16px] w-8 animate-pulse rounded-[3px] bg-edge" />
            <span className="block h-[11px] w-24 animate-pulse rounded bg-edge" />
          </div>
          <div className="flex flex-col gap-2">
            <span className="block h-[14px] w-full animate-pulse rounded bg-edge" />
            <span className="block h-[14px] w-[95%] animate-pulse rounded bg-edge" />
            <span className="block h-[14px] w-[70%] animate-pulse rounded bg-edge" />
          </div>
        </div>
      </div>

      {/* Contact card */}
      {/* Mirrors the real card, which no longer carries an accent rail — a
          skeleton with 8px of padding the loaded card lacks makes the content
          slide left the moment it arrives. */}
      <div className="mt-5 overflow-hidden rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
        <div>
          <span className="mb-3 block h-[16px] w-40 animate-pulse rounded bg-edge" />
          <span className="mb-4 block h-[13px] w-[78%] animate-pulse rounded bg-edge" />
          <span className="block h-11 w-full animate-pulse rounded-md bg-edge sm:w-48" />
        </div>
      </div>
    </div>
  );
}

/** Budget + deadline facts card — the same compact block appears in the mobile
 *  under-title block and in the desktop sticky rail. The budget headline is the
 *  compact locale form ("1,9 млрд"); the exact grouped value is one hover away
 *  via the `title` tooltip. */
/** Russian day-count word (день/дня/дней) or uz 'kun'. */
function RailFacts({
  budgetUzs,
  locale,
  deadline,
  daysLeftN,
  kind,
  hideBudget = false,
  announcement = false,
  bidsOpenOn = null,
}: {
  budgetUzs: string | null;
  locale: string;
  deadline: string | null;
  daysLeftN: number | null;
  /** Includes 'closed': a finished lot must not render urgency styling. */
  kind: 'active' | 'closing' | 'closed';
  /** «Анонс» — bids not open yet, no deadline: the badge replaces the value. */
  announcement?: boolean;
  /** «Приём с …» — acceptance has not started yet (already formatted, and only
   *  once hydrated: whether it is still ahead depends on the client clock). */
  bidsOpenOn?: string | null;
  /** Supply-scope (cooperation.uz) lots carry no price until bidding closes —
   *  `budgetUzs` is always null for them at this stage, and a card that says
   *  "Бюджет: Не указан" reads as a data gap rather than "this lot has no
   *  upfront budget by design". Omit the card entirely rather than show a
   *  misleading placeholder. */
  hideBudget?: boolean;
}) {
  const t = useT();
  const compactBudget = formatBudgetCompact(budgetUzs, locale);
  const exactBudget = formatBudget(budgetUzs);
  return (
    <>
      {/* БЮДЖЕТ — its own card. */}
      {!hideBudget && (
        <div className="rounded-lg border border-edge bg-bone-surface p-4 shadow-m-sm">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
            {t('tender.budgetLabel')}
          </div>
          {compactBudget ? (
            <div className="flex items-baseline gap-1.5">
              <span
                title={exactBudget ?? undefined}
                className="text-[22px] font-bold tabular-nums tracking-[-0.3px] text-brand"
              >
                {compactBudget}
              </span>
              <span className="text-[12px] font-medium text-ink-muted">
                {t('tender.budget.unit')}
              </span>
            </div>
          ) : (
            <div className="text-[14px] text-ink-muted">{t('tender.budget.undisclosed')}</div>
          )}
        </div>
      )}
      {/* СРОК — its own card, with a days-left badge. */}
      <div className="rounded-lg border border-edge bg-bone-surface p-4 shadow-m-sm">
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
            {t('tender.deadlineLabel')}
          </div>
          {daysLeftN != null && (
            <span
              className={`shrink-0 rounded-pill px-2 py-0.5 text-[11px] font-bold ${
                kind === 'closing' ? 'bg-orange/15 text-rust' : 'bg-brand-dim text-brand'
              }`}
            >
              {daysLeftN} {daysWord(daysLeftN, locale)}
            </span>
          )}
        </div>
        {announcement ? (
          <span className="mt-1 inline-block rounded-pill bg-brand-dim px-2.5 py-1 text-[12px] font-bold text-brand">
            {t('tender.deadline.announcement')}
          </span>
        ) : (
          <div
            className={`text-[16px] font-semibold sm:text-[18px] ${
              kind === 'closing' ? 'text-status-closing-text' : 'text-ink'
            }`}
          >
            {deadline ?? t('tender.deadline.tba')}
          </div>
        )}
        {bidsOpenOn && (
          <div className="mt-1 text-[12px] font-semibold text-brand">
            {t('tender.bidsOpen.from', { date: bidsOpenOn })}
          </div>
        )}
      </div>
    </>
  );
}

/** «Анонс» — the one line saying why there is no deadline yet and what to do
 *  about it (☆). Full width under the facts on mobile, in the rail on desktop. */
function AnnouncementNote() {
  const t = useT();
  return (
    <p className="rounded-lg border border-edge bg-bone-surface p-4 text-[13px] leading-[1.45] text-ink-muted shadow-m-sm">
      {t('tender.deadline.announcementNote')}
    </p>
  );
}

/** An invitation to ONE named supplier (goszakup «из одного источника по
 *  несостоявшимся закупкам»): who may bid, said plainly, beside the facts. */
function SingleSupplierNote() {
  const t = useT();
  return (
    <p className="rounded-lg border border-edge bg-bone-surface p-4 text-[13px] leading-[1.45] text-ink-muted shadow-m-sm">
      {t('tender.singleSupplier.note')}
    </p>
  );
}

/** The single primary action, reflecting the viewer's wall: guests register,
 *  free users upgrade, unlocked/Pro viewers jump to the revealed contacts.
 *  Rendered in the rail (desktop), the under-title block, and the sticky mobile
 *  bottom bar — same component, so the CTA never drifts between surfaces. */
function PrimaryCta({
  gateState,
  unlocked,
  tenderId,
  className = '',
}: {
  gateState: TenderGateState;
  unlocked: boolean;
  /** The lot this CTA converts FROM — rides to /plans as `?tender`, so the
   *  buyer is returned to it once they have paid. */
  tenderId?: string;
  className?: string;
}) {
  const t = useT();
  // Only meaningful on the guest branch, which goes to /register: it is what
  // brings the visitor back to THIS lot after signing up instead of dropping
  // them on the catalogue. The Pro branch carries the tender in `?tender=`
  // already, so it is left alone.
  const authOrigin = useAuthOrigin();
  const base =
    'inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] text-center text-[14px] font-bold text-white transition-colors hover:bg-brand-mid';

  // Nothing to "open": Pro/unlocked viewers already have the contacts on the page,
  // and free users can't open them at all (Pro-only). The primary CTA exists only
  // to convert — guests register, free users upgrade — so unlocked shows none.
  if (unlocked) return null;
  const to = gateState === 'guest' ? '/register' : plansPathWithContext('paywall', tenderId);
  const label = gateState === 'guest' ? t('tender.cta.register') : t('tender.cta.pro');
  // Upgrade-intent signal — only the Pro CTA (not the guest → /register one).
  // Fires from every surface this renders on, mobile sticky bar included.
  const onUpgrade =
    gateState === 'guest' ? undefined : () => track('upgrade_cta_clicked', { source: 'tenderCta' });
  return (
    <Link
      to={to}
      state={gateState === 'guest' ? authOrigin : undefined}
      onClick={onUpgrade}
      className={`${base} ${className}`}
    >
      {label}
      <ArrowRight className="h-3.5 w-3.5 shrink-0" />
    </Link>
  );
}

/** The ONE action a supply-scope (cooperation.uz reverse-auction) lot offers —
 *  its outbound deep-link to the original lot on `new.cooperation.uz`. These
 *  lots carry no budget and no заказчик contact (the price only emerges once
 *  bidding closes), so unlike the general `sourceUrl` link below (which stays
 *  silent for a free viewer, because a gov/ifi tender still has a contact wall
 *  to pitch instead), hiding this for a free viewer would leave the page with
 *  no Pro pitch at all. A locked CTA is shown instead — same label, routes to
 *  `/plans` — rather than nothing.
 *
 *  `sourceUrl` is null for a non-Pro viewer regardless (the API omits
 *  `sourceKey`/`externalId` server-side for every layer — see
 *  `source-links.ts`), so the `unlocked` flag and `sourceUrl`'s presence never
 *  disagree in practice; both are checked so an entitled viewer whose payload
 *  is missing/unroutable never sees a dead-end link. */
function OriginalCta({
  unlocked,
  sourceUrl,
  supply,
  tenderId,
  metered = false,
}: {
  unlocked: boolean;
  sourceUrl: string | null;
  /** Rides to /plans as `?tender` — see PrimaryCta. */
  tenderId?: string;
  /** cooperation.uz reverse-auction lot — its own label and funnel source. */
  supply: boolean;
  /** ИП tier: has opens to spend on this lot. The locked → /plans variant is
   *  suppressed for them — they are a paying subscriber, and the control that
   *  actually gets them the link is the reveal card, not an upgrade pitch. */
  metered?: boolean;
}) {
  const t = useT();
  const base =
    'inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md border border-edge bg-bone-surface px-[18px] text-[13px] font-semibold text-ink transition-colors hover:bg-bone-hover';
  const label = t(supply ? 'tender.openOriginalCoop' : 'tender.openOriginal');

  if (unlocked) {
    if (!sourceUrl) return null;
    return (
      <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className={base}>
        {label}
        <ArrowRight className="h-3.5 w-3.5 shrink-0" />
      </a>
    );
  }

  /* A metered subscriber gets NO locked variant: they have already bought
     access to this link and can spend an open on it right here, so a padlock
     routing them to /plans is a paywall shown to someone who is past it. The
     ContactRevealCard is their control. */
  if (metered) return null;

  /* 2026-09-06: shown LOCKED on every layer, not only supply. Until now a free
     viewer of a gov/ifi tender saw no «Открыть оригинал» at all, so the
     existence of the source link — one of the concrete things Pro buys — was
     invisible exactly to the people deciding whether to buy. The lock says
     what it costs; the click lands on /plans. `supplyOriginal` keeps its
     historical source name; the rest report `original`. */
  return (
    <Link
      to={plansPathWithContext('paywall', tenderId)}
      onClick={() =>
        track('upgrade_cta_clicked', { source: supply ? 'supplyOriginal' : 'original' })
      }
      className={base}
    >
      <span aria-hidden="true">🔒</span>
      {label}
    </Link>
  );
}

/** Тендерная документация for a viewer who is NOT entitled (2026-09-06): the
 *  real file list, name + type badge, each row locked, one CTA to /plans.
 *  Replaces the three hard-coded placeholder rows with nothing to click —
 *  the wall 13.5k people a month walked into and 4–5% clicked away from. Our
 *  source gives no file sizes, so none are shown rather than invented. */
/**
 * The metered (ИП) viewer's standing on THIS lot, as the documents card needs
 * it. `null` for everyone else — the card then sells Pro exactly as before.
 */
interface MeteredDocsAccess {
  /** An open was already spent here: the source link is theirs to use. */
  opened: boolean;
  /** Opens left this month, from the service that enforces them. */
  remaining: number;
  /** The lot's budget is over the tier's ceiling — only Pro can open it. */
  blocked: boolean;
}

/** «файл / файла / файлов»; invariant in Uzbek («fayl») and Kazakh («файл»). */
function filesWord(n: number, locale: string): string {
  if (locale === 'uz') return 'fayl';
  if (locale === 'kk') return 'файл';
  return n === 1 ? 'файл' : n >= 2 && n <= 4 ? 'файла' : 'файлов';
}

/** Scroll the reveal card into view — the SAME jump the sticky mobile bar makes,
 *  for the same reason it jumps rather than duplicating the button: the card
 *  owns pending/error/ceiling state, and a second copy of a control that SPENDS
 *  AN ALLOWANCE is exactly the thing that must not drift from the server. */
function jumpToRevealCard() {
  document.getElementById('sec-reveal')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function LockedDocumentsCard({
  docs,
  locale,
  tenderId,
  metered = null,
  sourceUrl = null,
  t,
}: {
  docs: { name: string; type?: string }[];
  locale: string;
  /** WHICH lot's documents were walled — see the `tenderId` note on
   *  AnalyticsTeaser. This emitter was the single largest source of
   *  `paywall_shown` rows with no tender on them. */
  tenderId?: string;
  /**
   * REPORTED BY A CUSTOMER, 2026-09-25 10:36 Ташкент (ИП subscriber, desktop,
   * «открыть не вышло»). The events tell the whole story: on one lot he pressed
   * this card's padlocks four times and the buyer-profile padlock once, each
   * press took him to /plans — where the ИП card has not been shown since
   * 19.09 — and he came back to the lot five times. The reveal card that
   * actually spends his open sat BELOW this card; he found it 36 minutes later
   * (`contact_revealed` 06:12 UTC). So for a metered viewer this padlock is a
   * paywall shown to someone past it, and worse, the most visible control on
   * the page pointed away from the one that works.
   *
   * With `metered` set the card offers the SAME action as the reveal card
   * (jump to it, spend one open, get the link — the files live on the source
   * portal, and an ИП open buys exactly that link), and once the open is
   * spent it turns into the outbound link itself. The Pro pitch stays only
   * where Pro really is the answer: over the tier's budget ceiling, or with
   * the month's opens gone.
   */
  metered?: MeteredDocsAccess | null;
  /** The source-portal link — rendered as the row target once `metered.opened`. */
  sourceUrl?: string | null;
  t: TFn;
}) {
  const plansTo = plansPathWithContext('paywall', tenderId);
  // The two metered branches. `canSpend` mirrors the reveal card's own
  // readiness (`!blocked && !exhausted`), so this card never promises an open
  // the card below would refuse.
  const canSpend = !!metered && !metered.opened && !metered.blocked && metered.remaining > 0;
  const spent = !!metered && metered.opened;
  // Only the Pro pitch is a paywall. Same rule as the reveal card's own
  // `paywall_shown`: an impression with no possible upgrade conversion would
  // sit in the denominator of the wall that does convert.
  const isPaywall = !canSpend && !spent;
  // The wall event this card replaces — same name, same `wall`, so the funnel
  // series does not break at the redesign.
  useEffect(() => {
    if (!isPaywall) return;
    track('paywall_shown', { wall: 'documents', ...(tenderId ? { tenderId } : {}) });
  }, [isPaywall, tenderId]);
  const n = docs.length;
  const word = filesWord(n, locale);
  const rowClass =
    'group flex w-full items-center gap-3 rounded-lg border border-edge-light px-3 py-2.5 text-left transition-colors hover:border-edge hover:bg-bone/50';
  const ctaClass =
    'mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] text-[14px] font-bold text-white transition-colors hover:bg-brand-mid';
  const rowBody = (doc: { name: string }, glyph: string | null) => {
    const b = docBadge(doc.name);
    return (
      <>
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[9px] font-extrabold tracking-[0.02em] text-white ${b.cls}`}
        >
          {b.label}
        </span>
        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink-soft">
          {doc.name}
        </span>
        {glyph && (
          <span aria-hidden="true" className="shrink-0 text-[13px] text-ink-muted/70">
            {glyph}
          </span>
        )}
      </>
    );
  };
  return (
    <div className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
          {t('tender.documentsLabel')}
        </div>
        <div className="text-[12px] text-ink-muted">
          {n} {word}
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        {docs.map((doc, i) =>
          canSpend ? (
            <button key={i} type="button" onClick={jumpToRevealCard} className={rowClass}>
              {rowBody(doc, '↓')}
            </button>
          ) : spent && sourceUrl ? (
            <a
              key={i}
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={rowClass}
            >
              {rowBody(doc, '↗')}
            </a>
          ) : spent ? (
            <div key={i} className={rowClass}>
              {rowBody(doc, null)}
            </div>
          ) : (
            <Link
              key={i}
              to={plansTo}
              onClick={() => track('upgrade_cta_clicked', { source: 'documentsLocked' })}
              className={rowClass}
            >
              {rowBody(doc, '🔒')}
            </Link>
          ),
        )}
      </div>
      {canSpend && metered ? (
        <>
          <p className="mt-3 text-[13px] leading-snug text-ink-muted">
            {t('tender.docsTeaser.meteredHint')}
          </p>
          <button type="button" onClick={jumpToRevealCard} className={ctaClass}>
            {t('tender.docsTeaser.meteredCta', { count: metered.remaining })}
          </button>
        </>
      ) : spent ? (
        sourceUrl && (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className={ctaClass}>
            {t('tender.docsTeaser.meteredOpenedCta')}
            <ArrowRight className="h-3.5 w-3.5 shrink-0" />
          </a>
        )
      ) : (
        <Link
          to={plansTo}
          onClick={() => track('upgrade_cta_clicked', { source: 'documentsLocked' })}
          className={ctaClass}
        >
          <span aria-hidden="true">🔒</span>
          {t('tender.docsTeaser.cta')}
        </Link>
      )}
    </div>
  );
}

const DOC_BADGE_COLORS: Record<string, string> = {
  PDF: 'bg-[#c0442e]',
  DOC: 'bg-[#2f5496]',
  XLS: 'bg-[#217346]',
  PPT: 'bg-[#c43e1c]',
  RAR: 'bg-[#6d5bd0]',
  ZIP: 'bg-[#6d5bd0]',
};

/** File-type badge (label + colour) from a filename's extension. */
function docBadge(name: string): { label: string; cls: string } {
  const ext = (name.split('.').pop() ?? '').toLowerCase();
  const label =
    ext === 'docx' ? 'DOC' : ext === 'xlsx' ? 'XLS' : ext === 'pptx' ? 'PPT' : ext.toUpperCase();
  return { label: label || 'FILE', cls: DOC_BADGE_COLORS[label] ?? 'bg-ink-muted' };
}

/** Тендерная документация — colour-coded file rows + download. Our source gives
 *  raw filenames and no size, so we show the filename and omit the weight rather
 *  than invent it. */
function DocumentsCard({
  docs,
  apiBase,
  locale,
  t,
}: {
  docs: { name: string; url: string }[];
  apiBase: string;
  locale: string;
  t: TFn;
}) {
  const n = docs.length;
  const word = filesWord(n, locale);
  return (
    <div className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
          {t('tender.documentsLabel')}
        </div>
        <div className="text-[12px] text-ink-muted">
          {n} {word}
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        {docs.map((doc, i) => {
          const b = docBadge(doc.name);
          const href = doc.url.startsWith('http') ? doc.url : `${apiBase}${doc.url}`;
          return (
            <a
              key={i}
              href={href}
              target="_blank"
              // nofollow: the download proxy is not a page. Googlebot followed
              // these into /api/documents/download and logged 32 blocked + 8
              // unauthorized crawls (GSC, 2026-09-07); robots.txt now disallows
              // /api/ as well.
              rel="noopener noreferrer nofollow"
              className="group flex items-center gap-3 rounded-lg border border-edge-light px-3 py-2.5 transition-colors hover:border-edge hover:bg-bone/50"
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[9px] font-extrabold tracking-[0.02em] text-white ${b.cls}`}
              >
                {b.label}
              </span>
              <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink">
                {doc.name}
              </span>
              <DownloadIcon className="h-4 w-4 shrink-0 text-ink-muted transition-colors group-hover:text-brand" />
            </a>
          );
        })}
      </div>
    </div>
  );
}

export function TenderDetail() {
  // The route param may be slugged (`{slug}-{id}`) or a bare cuid — recover the id.
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? extractTenderId(idParam) : undefined;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = useLocale();
  const t = useT();
  const hydrated = useHydrated();
  usePageTitle(t('nav.tender'));
  const { status } = useAuth();
  const isAuthed = status === 'authed';

  // Funnel (TND-8): record tender opens for signed-in users.
  useEffect(() => {
    if (status !== 'authed' || !id) return;
    trackEvent('tender_opened_on_site', { id });
  }, [status, id]);

  /*
   * Alert deep-link opens (the `?a=1` marker the notifier adds) go through the
   * PUBLIC beacon, and are NOT gated on auth.
   *
   * This is the activation metric, and it was being measured through a channel
   * that filtered out the channel it measures. `trackEvent` posts to
   * `/events/track`, which is cookie-authed; alert links are opened in the
   * Telegram in-app browser, which usually carries no session, so the request
   * 401'd and the open vanished. Prod showed 104 such 401s in a fortnight
   * against 82 recorded opens — i.e. roughly a third of every alert click was
   * being dropped, which is most of why activation read 3.2%.
   *
   * `alert_opened` was already in PUBLIC_EVENTS server-side; only the client
   * was pointed at the wrong endpoint.
   */
  useEffect(() => {
    if (!id || searchParams.get('a') !== '1') return;
    track('alert_opened', { id });
  }, [id, searchParams]);

  /*
   * A lot opened from a renewal reminder (`?src=renewal`). The reminders for
   * one-time buyers lead with lots since 2026-10-06, and «every link tracked»
   * (Igor) means the lot links count as an open, not only the /plans button —
   * /plans records its own (plans.tsx). Public beacon for the same reason as
   * `alert_opened` above. `utm_campaign` names the rung (renewal_t7 / _t0 /
   * _winback), carried as `campaign`.
   */
  useEffect(() => {
    if (!id || searchParams.get('src') !== 'renewal') return;
    const campaign = searchParams.get('utm_campaign');
    track('renewal_reminder_opened', {
      id,
      target: 'lot',
      ...(campaign ? { campaign } : {}),
    });
  }, [id, searchParams]);

  // Conversion funnel: a tender open by ANY viewer (anonymous included), via the
  // public beacon keyed on the visitor id.
  useEffect(() => {
    if (id) track('tender_viewed', { id });
  }, [id]);

  const { data, isLoading, isError, error, errorUpdatedAt, failureCount, refetch } = useQuery({
    queryKey: ['catalog', 'tender', id, locale],
    queryFn: () => fetchTender(id!, locale),
    enabled: !!id,
    // NOT `retry: false`. This query used to give up after a single attempt and
    // then tell the reader the tender had been DELETED — see `tender-error.ts`
    // for the prod case that motivated the split. A 404 is still final (the
    // predicate refuses it); a dropped connection or a 5xx gets two more tries,
    // 300ms then 600ms apart, the same ladder `/auth/me` uses. Under
    // SSR/prerender the predicate refuses everything, so the ~5k-page prerender
    // job keeps its current wall-time.
    retry: shouldRetryTenderQuery,
    retryDelay: tenderRetryDelay,
  });
  const queryClient = useQueryClient();

  // Which error block to render — a real not-found, or a load that failed.
  // Null while the query is healthy.
  const errorKind = isError ? tenderErrorKind(error) : null;

  /*
   * No lot to show BECAUSE there is no network (22.10, Redmi offline). TanStack
   * Query PAUSES a query after the browser's `offline` event — `isLoading` is
   * false (it is `isPending && isFetching`) and so is `isError` — and the page
   * had no state for that: header, back link, footer, and nothing between them.
   * A page OPENED offline is the other shape: the query manager has heard no
   * event, tries, and settles as a `network` error. Both are "no lot, no
   * network", so both key on the browser's own answer rather than on the
   * query's status. On `online` the query resumes (or refetches) by itself and
   * the lot renders, which is kept; this only says what is going on meanwhile.
   * A definitive 404 keeps its own card — it does not depend on the network.
   */
  const online = useOnline();
  const waitingForNetwork = !online && data === undefined && errorKind !== 'not_found';

  /**
   * A failed load used to write NOTHING anywhere: no event, no counter, no log.
   * The only reason the 2026-09-09 case is known at all is that the customer
   * said so and his session showed a tender open with nothing after it.
   *
   * Keyed on `errorUpdatedAt`, which query-core stamps ONCE per settled
   * failure — retry attempts bump `failureCount`, not `error` — so this fires
   * per failed LOAD, not per attempt and not per render. A «Повторить» that
   * fails again IS a new failed load and is meant to fire again: how often the
   * retry also fails is the number worth having.
   */
  useEffect(() => {
    if (!id || !isError) return;
    /*
     * `attempt` — how many tries this LOAD took before it gave up (1 for a
     * 404, which is never repeated; 3 for a network failure or a 5xx, which
     * get the full ladder). Without it the retry is unmeasurable: a row saying
     * `reason:'network'` reads the same whether one attempt failed or three
     * did, so "how often does the retry rescue the reader" — the only question
     * that decides whether the ladder should be longer, shorter or gone — has
     * no denominator. Read at settle time, which is when this effect runs, so
     * it is the FINAL count and not a snapshot mid-ladder.
     */
    track('tender_load_failed', { id, reason: tenderErrorReason(error), attempt: failureCount });
    // `error` and `failureCount` are deliberately out of the deps —
    // `errorUpdatedAt` changes at exactly the moment the query settles, and
    // either of the others would refire on identity churn or on every rung of
    // the back-off ladder.
  }, [id, isError, errorUpdatedAt]);

  // Spends this tender's free monthly unlock. `unlockTender` throws (ApiError)
  // on ineligibility/exhaustion — left to propagate so `FreeUnlockGate` can
  // show its own error state; nothing to catch here.
  //
  // ⚠️ Prefix ['catalog','tender',id] — NOT ['tender',id] and NOT the full key
  // with `locale` appended. The detail query is keyed `['catalog','tender',
  // id, locale]`; invalidating the shorter prefix refreshes BOTH cached
  // locales rather than leaving the other stale until a language switch. Same
  // reasoning as the metered reveal's invalidation in ContactRevealCard.
  //
  // The two sidebar analytics panels (`BaseRatesPanel`, `BuyerGraphPanel`)
  // self-fetch on their OWN query keys — `['base-rates', id]` / `['buyer-graph',
  // id]` — so the detail invalidation alone leaves them showing their cached
  // `locked` result until a full reload. Invalidate them too so a spent unlock
  // lights the buyer/supplier analytics inline, same as contact + documents.
  async function handleFreeUnlock() {
    await unlockTender(id!);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['catalog', 'tender', id] }),
      queryClient.invalidateQueries({ queryKey: ['base-rates', id] }),
      queryClient.invalidateQueries({ queryKey: ['buyer-graph', id] }),
      queryClient.invalidateQueries({ queryKey: ['similar-winners', id] }),
    ]);
  }

  // Where "back" goes. A donor lot lives in its own section, so returning a
  // reader to the government catalog strands them in a list their tender isn't
  // in. GET /catalog/:id no longer refuses an `ifi` lot to a non-Pro caller
  // (that layer-wide 403 `pro_required` wall is gone — donor rows serve like
  // gov rows), so the payload's own `scope` is the only signal needed here.
  const fromIfi = data?.scope === 'ifi';
  // Point at `/` (the canonical home = catalog), not `/catalog` — the latter is a
  // deprecated dupe that 301s to `/`, so linking it wastes a redirect hop.
  const backTo = fromIfi ? '/ifi' : '/';
  const backLabel = fromIfi ? t('tender.backToIfi') : t('tender.backToCatalog');

  // Full UZ+RU AI summary — Pro-only, fetched on demand once this tender is
  // unlocked (see the effect below). A 403 surfaces via the error path.
  const fullSummary = useQuery({
    queryKey: ['full-summary', id],
    queryFn: () => fetchFullSummary(id!),
    enabled: false,
    retry: false,
  });
  // The unlocked full text in the active locale (fall back across langs). Null
  // when not yet fetched OR the API returned an empty {uz:null,ru:null}.
  const fullText = fullSummary.data
    ? ((locale === 'ru' ? fullSummary.data.ru : fullSummary.data.uz) ??
      fullSummary.data.uz ??
      fullSummary.data.ru)
    : null;

  // The metered middle (ИП): pays, but buys a monthly quota rather than the
  // whole product. `unlocked` is a boolean and cannot say that, so before this
  // an ИП subscriber fell into `gateState === 'free'` and was shown the same
  // hard Pro wall as someone who has paid nothing — with no way to spend the
  // opens they had bought. `contactAccess` comes from the service that ENFORCES
  // the quota, so the control offered here and the rule applied on write cannot
  // disagree.
  const access = data?.contactAccess ?? null;
  const metered = access?.mode === 'metered';

  // The server sets `unlocked` for a viewer entitled to the paid surface of
  // THIS tender: a full-access subscriber, someone who spent the free monthly
  // unlock here — and, since the ИП fix, a metered subscriber who has spent one
  // of their opens on this lot (they bought the contact and the source link).
  const unlocked = !!data?.unlocked;
  // Full access specifically. `unlocked` no longer implies it, and the two
  // surfaces below are genuinely Pro-only: the full-summary endpoint refuses a
  // metered caller with 403, and the buyer profile has its own Pro wall — so
  // sending an ИП viewer to either would fail after the click, not before it.
  const proUnlocked = unlocked && !metered;
  const contact = data?.contact ?? null;
  // Is there anything IN it? A metered open on a lot with no заказчик contact
  // still ships `{name:null,email:null,phone:null}` — a truthy object with
  // nothing to call. The КОНТАКТНОЕ ЛИЦО card already tested the fields; the
  // sticky mobile bar tested only the object, and would have drawn a «Контакты»
  // anchor pointing at a card that never rendered.
  const hasRealContact = !!(contact && (contact.name || contact.phone || contact.email));

  // The full AI summary lives behind its own on-demand endpoint (`enabled:
  // false`); pull it in as soon as this tender is unlocked (Pro), so the full
  // text appears without a manual step.
  useEffect(() => {
    if (proUnlocked && !fullSummary.data && !fullSummary.isFetching && !fullSummary.isError) {
      void fullSummary.refetch();
    }
    // Keyed on `proUnlocked` alone (fetch once when it flips true).
  }, [proUnlocked]);

  // Value realization: the AI summary text as actually rendered on screen, for
  // EVERY viewer — guest, free, Pro. Until 2026-09-09 this fired from the
  // Pro-gated `/summary` fetch above, which only ever ran for an `unlocked`
  // (Pro) viewer; measured over 90 days, 76 of the 77 payers who fired it were
  // already Pro at the moment of the view, making "summary readers convert"
  // tautological. The FREE render is `data.description` (ships unconditionally
  // — see `catalog.service.ts` `resolveSummary()` / Item 6 above), so key off
  // that directly rather than the Pro fetch. Gated on the summary text itself,
  // not `overviewDescription` (which always has a deterministic-facts
  // fallback), so a tender with no summary yet doesn't count as "read". A ref
  // latches per tender id so a re-render — or React StrictMode's double-invoke
  // — isn't counted as a second read.
  const summaryViewedRef = useRef<string | null>(null);
  useEffect(() => {
    const summary = data?.description?.trim();
    if (!id || !summary || summaryViewedRef.current === id) return;
    summaryViewedRef.current = id;
    track('full_summary_viewed', { id, locale });
  }, [id, data?.description, locale]);

  // Gate state for the unified TenderGateCard — pure two-wall model. Guests are
  // pitched registration; every authed non-Pro viewer hits the same hard Pro
  // wall; Pro renders no gate (the caller shows the unlocked content instead).
  const gateState: TenderGateState = !isAuthed ? 'guest' : unlocked ? 'pro' : 'free';

  // ⚠️ Offered even when the tender carries NO заказчик contact.
  //
  // It used to require `hasContact !== false`, on the reasoning that an open
  // must not be spent on nothing. That is right for the free lifetime cap and
  // wrong for the meter: an ИП open buys the contact AND the link to the
  // original notice, so on a contact-less lot the link is simply the whole of
  // what it buys — and those are exactly the lots where a subscriber has no
  // other way to reach the source. The card says which of the two it is
  // (`hasContact`), and the endpoint applies the same split.
  //
  // Live reach on 2026-09-10 — contact-less lots by scope: gov 0/3,872,
  // corporate 85/120, ifi 30/87, supply 1,251/1,251. `gov` is unaffected;
  // everywhere else this is the difference between a button and nothing.
  //
  // ⚠️ `access.limited` ALSO renders this card, for any mode.
  //
  // A viewer who hit the per-account read ceiling is entitled to the contact
  // and simply did not get it this time: the server withheld it and `contact`
  // is null. Without this branch nothing at all would render — `showGate` is
  // false because they are `unlocked`, and the КОНТАКТНОЕ ЛИЦО card below needs
  // a contact to draw. The page would silently lose a section and the reader
  // would have no idea why. `ContactRevealCard` renders the calm explanation
  // (and no Pro CTA) for that state.
  const showReveal = !!id && (!!access?.limited || (access?.mode === 'metered' && !access.opened));

  // NO EMPTY PAYWALL. The wall only earns its place when something real sits
  // behind it — the заказчик contact or the tender documents. Reuses the SAME
  // non-sensitive existence-flag mechanism the blurred teasers already use
  // (`hasDocuments`, `hasRequirements`, and the analytics panels' own
  // `hasData`), rather than a second notion of "is there anything here".
  //
  // Load-bearing for the donor (`ifi`) layer: ADB, UNDP and AIIB publish no
  // buyer contact at all, and roughly a quarter of live donor notices carry
  // neither contact nor documents. A «Оформить Pro» CTA on one of those would
  // sell an unlock that reveals nothing. `gov` really is unaffected (0 of 3,872
  // live lots are contact-less, 2026-09-10); `corporate` is NOT — 85 of its 120
  // live lots carry no contact. CONTACT_GATE keeps those out of BROWSE, not off
  // their own detail page, which is reachable by URL. `hasContact` defaults to
  // true when absent (see `rowToItem`) — a missing flag must never strip a wall.
  const hasGatedContent = !!data && (data.hasContact !== false || !!data.hasDocuments);
  // The gate card renders only for a non-Pro viewer AND only when there is
  // something to unlock — the same condition the JSON-LD paywall node and the
  // sticky mobile CTA key off, so the three can never disagree.
  // ⚠️ A metered viewer must NOT also get the hard Pro wall.
  //
  // `unlocked` stays false for an ИП subscriber even after they spend an open,
  // so the wall rendered next to the reveal card — and kept rendering beside
  // the contact they had just paid for, telling them «Оформите Pro — контакты
  // заказчика…» while the contact sat above it. The inline gate was made
  // exclusive when the reveal card landed; the sticky mobile bar was not, which
  // is where it showed most.
  const showGate = !unlocked && !metered && hasGatedContent;

  // Public deep-link to the original tender on the source platform. Prefer a
  // stored `sourceUrl` (captured at harvest — needed for sources like ebirja,
  // a Next.js SPA whose public URL uses an internal short id that can't be
  // derived from sourceKey+externalId) over the derived switch (null when the
  // source's route isn't verified yet — no dead links).
  const sourceUrl = data
    ? (data.sourceUrl ?? sourceTenderUrl(data.sourceKey, data.externalId))
    : null;

  // Supply-scope (cooperation.uz reverse-auction) lots: no budget, no заказчик
  // contact — the outbound link to the original lot is the ONE action, and it
  // is Pro-gated (see OriginalCta above — since 2026-09-06 every layer shows it).
  const isSupply = data?.scope === 'supply';

  // Public per-lot supply/commercial detail (goszakup, etender). Display-only,
  // ungated — rendered as labelled rows below the overview, plus the position
  // list of a multi-position etender lot. See `lib/lot-details.ts`. Computed
  // before the procedure rows, which drop their place row when this has one.
  const lotDetail = (data?.lotDetails ?? null) as Record<string, unknown> | null;
  const lotRows = lotDetailRows(lotDetail, locale, t('tender.budget.unit'));
  const lotPos = lotPositions(lotDetail);

  // Harvested documentation + procedure metadata (detail view only).
  const docs = Array.isArray(data?.documents) ? data.documents : [];
  const proc = (data?.procedure ?? null) as Record<string, unknown> | null;
  // The announcement's own name («хлеб и т.д.») — rendered as a subtitle right
  // under the H1, not inside the procedure card (see the PROC_KEYS note
  // above): a one-word lot title («Дрожжи») means nothing without it, and
  // that has to be visible before a reader scrolls to «Порядок проведения».
  const announcementName =
    typeof proc?.announcementName === 'string' && proc.announcementName.trim()
      ? proc.announcementName
      : null;
  // «№ …» in the header, Kazakhstan only — see `LotNumberChip`. The PUBLIC
  // announcement number (also the «Номер объявления» row below), never the
  // Pro-only `lotNumber`.
  const headerNumber =
    KZ_NUMBER_IN_HEADER &&
    typeof proc?.announcementNumber === 'string' &&
    proc.announcementNumber.trim()
      ? proc.announcementNumber.trim()
      : null;
  const PROC_KEYS = [
    // Announcement-level facts (goszakup / mitwork, 2026-09-28): who runs the
    // parent purchase a one-word lot («Дрожжи») belongs to, and how.
    // ⚠️ `announcementName` deliberately NOT in this list — it renders as a
    // subtitle right under the H1 instead (see `announcementName` below),
    // since a name buried in the procedure card doesn't help a reader who
    // hasn't scrolled that far yet decide what a bare one-word title means.
    'announcementNumber',
    // ⚠️ `reference` (etender's registry number, «26120500018169») is
    // deliberately NOT in this list and never was: it is source identity, the
    // API sends it only to a viewer who may see the source, and that viewer
    // already has «Открыть лот на площадке». Do not add it without that gate.
    'method',
    'subjectKind',
    'planType',
    'organizer',
    'representative',
    'bidStart',
    'serviceType',
    'financialSource',
    'address',
    'deliveryAddress',
    'decree',
    'published',
    'positions',
    'discussionStart',
    'discussionEnd',
    'docsDeadline',
    'opening',
    'vatNote',
    'commission',
  ] as const;
  // Subset of PROC_KEYS that the harvesters populate as ISO date strings —
  // rendered raw they read as "2026-07-17"; localize them the same way as
  // the deadline/published facts below ("17 июля 2026").
  const DATE_PROC_KEYS = new Set([
    'published',
    'bidStart',
    'discussionStart',
    'discussionEnd',
    'docsDeadline',
    'opening',
  ]);
  const procRows: Array<[string, string]> = proc
    ? PROC_KEYS.map((k) => [k, proc[k]] as [string, unknown])
        .filter(
          (pair): pair is [string, string] =>
            typeof pair[1] === 'string' && pair[1].trim().length > 0,
        )
        // The delivery place once, not twice: «Условия поставки» already shows it.
        .filter(([k]) => !procedureRowDuplicatesLotPlace(k, lotRows))
        .map(([k, v]) => {
          if (DATE_PROC_KEYS.has(k) && !Number.isNaN(Date.parse(v))) {
            return [k, formatDeadline(v, locale) ?? v] as [string, string];
          }
          return [k, v] as [string, string];
        })
    : [];
  // Contract-fulfilment term (etender `deliveryTermDays` / tender-mc `workDays`)
  // — numeric, so outside the string-only PROC_KEYS loop above. Prepend it as
  // «Срок выполнения обязательств»; ~764 live tenders (~18%) carry it.
  const obligationTerm = formatObligationTerm(proc, locale);
  if (obligationTerm) procRows.unshift(['obligationTerm', obligationTerm]);

  // The card's identity line — see `lotIdentityParts`.
  const identity = lotIdentityParts({
    unlocked,
    sourceName: data?.sourceName ?? null,
    lotNumber: data?.lotNumber ?? null,
    method: procRows.find(([k]) => k === 'method')?.[1] ?? null,
    labels: { platform: t('tender.identity.platform'), method: t('tender.procedure.method') },
  });

  const kind = data ? statusKind(data.status) : 'active';
  const budget = data ? formatBudget(data.budgetUzs) : null;
  const deadline = data ? formatDeadline(data.deadline, locale) : null;
  // «Анонс» (xt-xarid pre-bidding): announced, bids not open, no deadline yet.
  const announcement = data?.announcement === true;
  // goszakup «из одного источника по несостоявшимся»: one invited supplier.
  const singleSupplier = data?.singleSupplier === true;
  // Client-only: the countdown is relative to NOW, and a prerendered page is
  // hours old — baking it into the HTML made every tender page hydrate with a
  // text mismatch (React #418), which discards and rebuilds the page on the
  // client and pushed Chrome's LCP from 1.2 s to 6.2 s (measured 2026-09-07).
  // The absolute date next to it is in the HTML; the badge appears on hydration.
  const dLeft = data && hydrated ? daysLeft(data.deadline) : null;
  // «Приём с …» — client-only like the countdown: «still ahead» reads NOW.
  const bidsOpenOn =
    data && hydrated && data.bidsOpenAt && Date.parse(data.bidsOpenAt) > Date.now()
      ? formatDeadline(data.bidsOpenAt, locale)
      : null;
  // Bidding over, no outcome published → the honest-status note and the
  // «Похожие открытые лоты» block (see `isClosedWithoutOutcome`). `now` only
  // once hydrated, for the same #418 reason as `dLeft` above: the deadline
  // half of the test reads the clock.
  const closedNoOutcome = data ? isClosedWithoutOutcome(data, hydrated ? Date.now() : null) : false;
  // The header pill follows it: a lot whose deadline passed an hour ago (the
  // expiry sweep has not run yet) must not keep saying «Активен» above a note
  // saying bidding is over. Every other lot keeps `kind` exactly as before.
  const pillKind = closedNoOutcome ? 'closed' : kind;
  // The old embedding-based «Похожие тендеры» at the bottom steps aside while
  // «Похожие открытые лоты» is (or may be about to be) on screen above — one
  // «similar» list per page, and the one that leads somewhere (Igor, 09.10).
  // Live lots and lots with an outcome keep it, and so does a closed lot whose
  // new block settled to nothing (fewer than three, or an error). `hydrated`
  // keeps the prerendered HTML exactly as before: the crawl links in it are
  // the old block's first job.
  const similarLiveShown = useSimilarLiveShown(
    id ?? '',
    locale,
    closedNoOutcome && hydrated && Boolean(id),
  );
  const hideOldSimilar = closedNoOutcome && hydrated && similarLiveShown !== false;
  // Mobile section-nav chips — only for sections that actually render on this
  // tender (mirrors the section guards below).
  const navSections = data
    ? ([
        // The single free overview (Описание/Спецификация/Требования) — always
        // renders (facts are a guaranteed fallback), so it always gets a nav chip.
        { id: 'sec-summary', label: t('tender.nav.description') },
        // Integrity panel renders only for authed viewers (and self-hides when
        // there's no data behind the wall); gate the chip on auth to match.
        isAuthed && { id: 'sec-integrity', label: t('tender.nav.integrity') },
        // Same server-decided guard as the section itself — a chip that scrolls
        // to nothing is worse than no chip.
        data.bidPackage && { id: 'sec-bid-package', label: t('tender.bidPackage.title') },
        (docs.length > 0 || data.hasDocuments) && {
          id: 'sec-documents',
          label: t('tender.nav.documents'),
        },
        (lotRows.length > 0 || lotPos.positions.length > 0) && {
          id: 'sec-lot-details',
          label: t('tender.nav.lotDetails'),
        },
        data.siblingLots &&
          data.siblingLots.length > 0 && { id: 'sec-siblings', label: t('tender.nav.siblings') },
        unlocked &&
          procRows.length > 0 && { id: 'sec-procedure', label: t('tender.nav.procedure') },
        contact && { id: 'sec-contacts', label: t('tender.nav.contacts') },
      ].filter(Boolean) as { id: string; label: string }[])
    : [];
  // The inline contact name often embeds the ИНН as "... (ИНН 123456789)";
  // split it so the org and its ИНН render on separate lines.
  const contactName = contact?.name ?? null;
  const innMatch = contactName ? contactName.match(/\(?\s*ИНН\s*[:№]?\s*(\d{6,})\s*\)?/i) : null;
  const contactInn = innMatch?.[1] ?? null;
  const contactOrg =
    innMatch && contactName ? contactName.replace(innMatch[0], '').trim() : contactName;
  const publishedRaw = typeof proc?.published === 'string' ? proc.published : null;
  const published =
    publishedRaw && !Number.isNaN(Date.parse(publishedRaw))
      ? formatDeadline(publishedRaw, locale)
      : null;

  // Deterministic key-facts paragraph — guarantees every tender page has
  // substantial, tender-specific prose even without an AI summary / description,
  // and powers the SEO meta + JSON-LD (Tier-2 completeness §8 #5).
  const facts = data
    ? buildTenderFacts(
        {
          title: data.title,
          region: data.region,
          budget,
          categories: data.categories.map((c) => c.name),
          deadline,
          published,
          docCount: data.documents?.length ?? 0,
        },
        t,
      )
    : '';

  // «Описание» text as actually rendered (summary → description → deterministic
  // facts), precomputed so we can suppress a «Спецификация» that merely repeats
  // it. Some sources carry `detail` identical to the description, which rendered
  // the same paragraph twice under two headings — duplicate text within one page
  // that adds no information for a reader or a crawler (SEO/content audit,
  // 2026-08-25). Compared whitespace/case-insensitively so trivial reflow differs.
  const overviewDescription = (fullText ?? data?.description)?.trim() || facts;
  const normText = (s: string | null | undefined) =>
    s?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';
  const overviewSpec =
    data?.detail && normText(data.detail) !== normText(overviewDescription) ? data.detail : null;

  // Absolute, locale-correct URL for a bare in-app path (for JSON-LD nodes).
  const absUrl = (p: string) => `${countryConfig.brand.siteUrl}${localePath(p, locale)}`;
  const hasCategory = Boolean(data?.categorySlug && data?.category);
  const jsonLd = data
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'GovernmentService',
            name: data.title,
            // Prefer the real description; fall back to the deterministic key-facts
            // paragraph so the node is never empty (from main's Tier-2 work).
            description: (data.description?.trim() || facts).slice(0, 500),
            ...(data.region
              ? { areaServed: { '@type': 'AdministrativeArea', name: data.region } }
              : {}),
            serviceType: data.categories.map((c) => c.name).join(', ') || undefined,
            ...(data.budgetUzs
              ? {
                  offers: {
                    '@type': 'Offer',
                    price: data.budgetUzs,
                    priceCurrency: countryConfig.currency.code,
                    // Submission deadline — the single most decision-relevant
                    // fact about a tender. `deadline` is an ISO-8601 string from
                    // the API, valid as-is for schema.org.
                    ...(data.deadline ? { availabilityEnds: data.deadline } : {}),
                  },
                }
              : {}),
            inLanguage: locale,
            // Stable public identifier — the cuid in the URL, the join key across
            // every internal reference to this lot.
            ...(id
              ? { identifier: { '@type': 'PropertyValue', propertyID: 'tenderId', value: id } }
              : {}),
            // The procuring authority — `provider` is the schema.org property
            // GovernmentService expects for "who runs this". When the detail
            // payload carries the buyer's public ИНН (9-digit, linkable), point
            // `url` at its `/buyer/{tin}` profile so the org resolves to an entity,
            // not just a name string.
            ...(data.buyerName
              ? {
                  provider: {
                    '@type': 'GovernmentOrganization',
                    name: data.buyerName,
                    ...(isLinkableTin(data.buyerTin)
                      ? { url: absUrl(`/buyer/${data.buyerTin}`) }
                      : {}),
                  },
                }
              : {}),
            // When the notice was published (ISO from the API) — the freshness
            // anchor a tender search result leads with.
            ...(data.publishedAt ? { datePosted: data.publishedAt } : {}),
            // Honest freshness signal for Google + AI engines: when the tender's
            // substance last changed (ISO from the API), not the every-cycle
            // updatedAt touch. Omitted for legacy rows with no value.
            ...(data.contentChangedAt ? { dateModified: data.contentChangedAt } : {}),
            ...(id ? { url: absUrl(tenderPath(data.slug, id)) } : {}),
          },
          // Declares the paywall to Google: the gated content (behind
          // `.tender-paid`, the gate card) is intentional — not cloaking/thin
          // content — so the page stays indexable. Emitted ONLY when that
          // element actually renders: a tender with nothing behind the wall
          // (no contact, no documents — common on the donor layer) has no
          // `.tender-paid` node, and declaring a paywall over a selector that
          // matches nothing is a false structured-data claim. The outcome wall
          // (`OutcomeWall`, 2026-10-07) carries `.tender-paid` too, so an
          // awarded lot with a walled winner declares it even when nothing
          // else on the page is gated.
          ...(hasGatedContent || data.outcomeLocked
            ? [
                {
                  '@type': 'WebPage',
                  isAccessibleForFree: false,
                  hasPart: {
                    '@type': 'WebPageElement',
                    isAccessibleForFree: false,
                    cssSelector: '.tender-paid',
                  },
                },
              ]
            : []),
          {
            // Navigational breadcrumb (Home → Category → Tender) — Google renders it
            // in the SERP and it clarifies site structure. Absolute per-locale URLs.
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: t('nav.catalog'), item: absUrl('/') },
              ...(hasCategory
                ? [
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: data.category,
                      item: absUrl(`/category/${data.categorySlug}`),
                    },
                  ]
                : []),
              {
                '@type': 'ListItem',
                position: hasCategory ? 3 : 2,
                name: data.title,
                ...(id ? { item: absUrl(tenderPath(data.slug, id)) } : {}),
              },
            ],
          },
        ],
      }
    : undefined;

  // Compact, localized budget for the <title> differentiator — "12 млн сум" /
  // "12 mln so'm". formatBudgetCompact never includes the unit word (so the exact
  // grouped value stays available in a tooltip elsewhere), so append it here.
  const seoCompactBudget = formatBudgetCompact(data?.budgetUzs ?? null, locale);
  const seoBudgetLabel = seoCompactBudget ? `${seoCompactBudget} ${t('tender.budget.unit')}` : null;

  // Prefer the tender's own description when substantial; else the key-facts
  // paragraph (always tender-specific) over the generic default.
  const rawDesc = data?.description?.trim();
  const seoDescription = data
    ? rawDesc && rawDesc.length > 60
      ? rawDesc.slice(0, 160)
      : facts.slice(0, 200)
    : t('tender.seoDescDefault');

  // A finished lot is worth indexing only if it says something no one else can:
  // who won and for how much (~15% of them). The rest are stale notices, near
  // identical to thousands of siblings. See isTenderIndexable for the full rationale.
  // While loading (`data` undefined) assume indexable — emitting noindex on a
  // page that turns out to be a live tender is the costlier mistake. But a
  // resolved ERROR is a soft-404: the shell renders an error block at HTTP 200,
  // so at minimum it must carry noindex rather than inviting Google to index a
  // URL that served no content. (A true 404 status would need an SSR status
  // channel that doesn't exist yet.)
  //
  // ⚠️ This covers BOTH error kinds, and must keep doing so. Splitting the
  // message («не найден» vs «не удалось загрузить») changed what the READER is
  // told; it changed nothing about what a crawler received, which is still 200
  // with no tender on the page. A transport failure that stayed indexable would
  // be the worse of the two — Google would cache an empty page for a LIVE lot.
  const noIndex = isError ? true : data ? !isTenderIndexable(data) : false;

  return (
    <>
      <Seo
        // Source titles are ';'-joined ИКПУ item lists (mean 108 chars, max
        // 1,114 in prod). Take only the FIRST item: measured over 100 live
        // tenders that moves the rendered title from 97/100 over Google's ~60
        // char budget to 0/100.
        //
        // The compact budget is appended as the differentiator: those first items
        // are ИКПУ product names, so 66.2% of indexed pages shared a title with
        // another page (484 of them rendered the same "Услуги по оптовой и
        // розничной торговле и…"). Adding the budget takes that to 37.0% while
        // keeping the median rendered title at 58 chars. See tender-seo-title.ts
        // for the full measurement, and for why the BUYER is still not appended.
        title={data ? buildTenderSeoTitle(data.title, seoBudgetLabel) : t('tender.loading')}
        description={seoDescription}
        // Keep the canonical STABLE across hydration. While data (hence the slug)
        // loads, fall back to the current URL param — NOT the slug-less
        // `/tender/{id}`, which briefly declared the cuid duplicate as canonical
        // (and dragged hreflang with it) before correcting to the slug. Prerendered
        // pages seed data and never flash; this guards the client-nav / non-
        // prerendered path so a mid-hydration snapshot can't capture the cuid.
        canonical={idParam ? (data ? tenderPath(data.slug, id!) : `/tender/${idParam}`) : undefined}
        lang={locale}
        jsonLd={jsonLd}
        noIndex={noIndex}
      />

      <div className="mx-auto max-w-3xl pb-24 lg:max-w-6xl lg:pb-0">
        <Link
          to={backTo}
          onClick={(e) => {
            // Return to the EXACT catalog view we came from (page + filters
            // from the URL, scroll from `useScrollRestoration`), not the bare
            // root which resets to page 1. The browser restores nothing here on
            // its own — native POP restoration only touches WINDOW scroll and
            // this shell scrolls an inner `<main>`, which is precisely why that
            // hook exists. Only when we have in-app history; a direct/SEO
            // landing (location.key === 'default') falls through to the href.
            // Keep the href so middle-click / new-tab / SEO still work.
            if (
              location.key !== 'default' &&
              e.button === 0 &&
              !e.metaKey &&
              !e.ctrlKey &&
              !e.shiftKey
            ) {
              e.preventDefault();
              navigate(-1);
            }
          }}
          className="mb-4 inline-block text-[13px] font-medium text-ink-soft transition-colors hover:text-brand"
        >
          {backLabel}
        </Link>

        {isLoading && !waitingForNetwork && <TenderDetailSkeleton />}

        {waitingForNetwork && (
          <div
            role="status"
            className="rounded-lg border border-edge bg-bone-surface p-8 text-center shadow-m-sm"
          >
            <p className="mb-1 text-[15px] font-semibold text-ink">{t('offline.title')}</p>
            <p className="mb-4 text-[13px] text-ink-muted">{t('offline.lotBody')}</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid sm:w-auto"
            >
              {t('error.retry')}
            </button>
          </div>
        )}

        {/* No layer refuses a detail payload any more (the `ifi` 403
            `pro_required` wall is gone), so an error here is either a genuine
            not-found or a transport failure — and those are NOT the same
            message. Only a definitive 404 may say the lot was deleted; a
            dropped connection or a 5xx says the load failed, because the lot is
            very probably still there (see `tender-error.ts`). Both keep the
            «Повторить» button: a retry is harmless on a 404 and is the whole
            point on the other. */}
        {isError && !isLoading && !waitingForNetwork && (
          <div className="rounded-lg border border-edge bg-bone-surface p-8 text-center shadow-m-sm">
            <p className="mb-1 text-[15px] font-semibold text-ink">
              {errorKind === 'not_found' ? t('tender.notFound') : t('tender.loadFailed')}
            </p>
            <p className="mb-4 text-[13px] text-ink-muted">
              {errorKind === 'not_found' ? t('tender.notFoundDesc') : t('tender.loadFailedDesc')}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-[13px] font-bold text-white transition-colors hover:bg-brand-mid sm:w-auto"
            >
              {t('error.retry')}
            </button>
          </div>
        )}

        {data && (
          <>
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_400px] lg:items-start lg:gap-6">
              <main className="flex min-w-0 flex-col gap-5">
                {/* Header — inverted brand card (Мок2): status + meta, white title,
                    ghost tag pills on dark teal. */}
                <div className="overflow-hidden rounded-lg bg-brand p-5 shadow-m-sm sm:p-7">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-pill border border-white/20 bg-white/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.05em] text-white">
                      <span
                        className={`h-[7px] w-[7px] rounded-full ${
                          pillKind === 'closing'
                            ? 'bg-orange'
                            : pillKind === 'closed'
                              ? 'bg-white/50'
                              : 'bg-status-active-dot'
                        }`}
                      />
                      {t(`tender.status.${pillKind}`)}
                    </span>
                    {/* The source's own lot number is NOT shown here: it makes the
                        lot one search away at the origin portal, where the contacts
                        we gate are free. Pro sees it in the identity line under the
                        title (2026-10-09), next to the source deep link.
                        Kazakhstan (2026-10-21) shows the ANNOUNCEMENT number
                        instead — public already, in «Порядок проведения» — as a
                        copy chip (`headerNumber`). */}
                    {/* «В избранное» — the same ☆ toggle the catalogue cards
                        carry (`tender_saved`, guest → login), so a lot opened
                        from a link or an alert can be kept without going back
                        to the list (owner, 2026-10-14). */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      {headerNumber && <LotNumberChip number={headerNumber} />}
                      {data.publishedAt && (
                        <span className="text-[12px] font-medium text-white/55">
                          {`${t('tender.publishedShort')} ${formatDeadline(data.publishedAt, locale)}`}
                        </span>
                      )}
                      {id && <SaveStar tenderId={id} onBrand withLabel />}
                    </div>
                  </div>

                  <h1
                    className={`font-brand text-[20px] font-extrabold leading-[1.18] tracking-[-0.3px] text-white [overflow-wrap:anywhere] sm:text-[25px] ${announcementName || identity.length > 0 ? 'mb-1' : 'mb-4'}`}
                  >
                    {data.title}
                  </h1>

                  {/* «Площадка · № лота · Тип процедуры». Platform and number
                      are Pro-only (the API omits them otherwise, and `unlocked`
                      gates them again here); the procedure type is public. */}
                  {identity.length > 0 && (
                    <p
                      data-testid="lot-identity"
                      className={`${announcementName ? 'mb-1' : 'mb-4'} text-[13px] font-medium text-white/70 [overflow-wrap:anywhere]`}
                    >
                      {identity.map((part, i) => (
                        <span key={part.key}>
                          {i > 0 && <span className="text-white/40"> · </span>}
                          {part.label && <span className="text-white/50">{part.label} </span>}
                          <span className={part.key === 'lotNumber' ? 'tabular-nums' : ''}>
                            {part.value}
                          </span>
                        </span>
                      ))}
                    </p>
                  )}

                  {/* The announcement's own name («хлеб и т.д.») — the only
                      text that says what a bare, one-word lot title («Дрожжи»)
                      actually belongs to. Self-hides when the source publishes
                      no announcement name (most non-KZ sources). */}
                  {announcementName && (
                    <p className="mb-4 text-[13px] font-medium text-white/70 [overflow-wrap:anywhere]">
                      {t('tender.procedure.announcementName')}: {announcementName}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-[6px]">
                    {data.region && (
                      <Link
                        to={`/region/${data.regionCode}`}
                        className="rounded-pill border border-white/25 px-2.5 py-1 text-[11px] font-semibold text-white/90 transition-colors hover:bg-white/10"
                      >
                        {data.region}
                      </Link>
                    )}
                    {data.categories.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        className="rounded-pill border border-white/25 px-2.5 py-1 text-[11px] font-semibold text-white/90 transition-colors hover:bg-white/10"
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Bidding over, no outcome yet: say so, then offer the live lots
                    of the same ЕНКТ code (ChatGPT audit 2026-10-08, item 1 —
                    53% of ChatGPT arrivals land on a finished lot). Lots WITH an
                    outcome never get here; their outcome block / wall below is
                    untouched. Client-only by construction: these lots are not
                    prerendered, and nothing here touches indexing. */}
                {closedNoOutcome && (
                  <>
                    <ClosedLotNotice deadline={deadline} />
                    {id && (
                      <SimilarLiveLots
                        tenderId={id}
                        locale={locale}
                        scope={data.scope}
                        followName={data.title.split(';')[0].trim().slice(0, 60)}
                      />
                    )}
                  </>
                )}

                {/* Mobile: budget/deadline + primary CTA directly under the title —
                    the rail's facts never collapse to the page bottom. */}
                <div className="flex flex-col gap-3 lg:hidden">
                  {/* Budget + deadline side by side — the two headline facts,
                      compact above the fold. Supply lots carry no budget, so the
                      lone deadline card spans full width instead. */}
                  {isSupply ? (
                    <RailFacts
                      budgetUzs={data.budgetUzs}
                      locale={locale}
                      deadline={deadline}
                      daysLeftN={dLeft}
                      kind={kind}
                      hideBudget
                      announcement={announcement}
                      bidsOpenOn={bidsOpenOn}
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <RailFacts
                        budgetUzs={data.budgetUzs}
                        locale={locale}
                        deadline={deadline}
                        daysLeftN={dLeft}
                        kind={kind}
                        announcement={announcement}
                        bidsOpenOn={bidsOpenOn}
                      />
                    </div>
                  )}
                  {announcement && <AnnouncementNote />}
                  {singleSupplier && <SingleSupplierNote />}
                  {/* «Открыть оригинал» FIRST — the control that sells (883
                      clicks / 773 people over 14 days to 28.09) — then the
                      bid-package waitlist, outlined and padlocked like it
                      (owner, 2026-09-28; until then the waitlist sat above,
                      filled in brand colour). Both self-hide where they must:
                      the waitlist for a granted account and a finished lot. */}
                  <OriginalCta
                    unlocked={unlocked}
                    sourceUrl={sourceUrl}
                    supply={isSupply}
                    tenderId={id}
                    metered={metered}
                  />
                  {BID_WAITLIST_VISIBLE && id && (
                    <BidWaitlistButton
                      tenderId={id}
                      granted={!!data.bidPackage}
                      closed={kind === 'closed'}
                      deadline={data.deadline}
                    />
                  )}
                  {/* The procedure type used to sit here in its own mobile-only
                      card; since 2026-10-09 it is part of the identity line under
                      the title, on every breakpoint (see `lotIdentityParts`). */}
                </div>

                {/* Sticky mobile section-nav — anchor jumps; all sections stay in DOM. */}
                <MobileSectionNav sections={navSections} />

                {/* Понимание тендера в ОДНОМ бесплатном блоке: «Описание» (разбор,
                    если есть, иначе детерминированные факты) + «Спецификация»
                    (позиции источника) + «Требования» (квалификация / тех.
                    характеристики / сроки / оплата — «предмет» опущен, он и есть
                    «Описание»). Всё бесплатно — платный слой это контакты /
                    документы / аналитика, а не чтение тендера. Без пометки «AI». */}
                <TenderOverview
                  description={overviewDescription}
                  specification={overviewSpec}
                  specIsSourceFallback={data.descriptionIsSourceFallback}
                  requirements={data.requirements}
                  requirementsState={data.requirementsState}
                  requirementsDisqualifying={data.requirementsDisqualifying}
                  /* The platform's name is source identity, gated with the
                     link: the API sends it only to a viewer who may see the
                     source, and `unlocked` gates it again here so a web build
                     that lands ahead of the API cannot print it to a guest. */
                  requirementsSourceName={unlocked ? data.requirementsSourceName : null}
                  sourceLocked={!unlocked}
                  /* Kazakhstan: a lot with no file at all shows no
                     requirements block (owner, 2026-10-21). `false`, not a
                     missing flag — an older API leaves the sentence alone. */
                  hideNoDocuments={KZ_QUIET_REQUIREMENTS && data.hasDocuments === false}
                  /* ⚠️ Both links carry the EXISTING gates, unchanged. The
                     source deep link rides `unlocked && sourceUrl` exactly like
                     `OriginalCta` below (the pair resolves to the lot on the
                     origin portal, where the contacts we sell are free — see
                     `source-links.ts`), and `docs` is only ever populated for an
                     entitled viewer. A guest gets the sentence without a link,
                     which is still the truth instead of an empty block. */
                  sourceHref={unlocked ? sourceUrl : null}
                  documentHref={
                    docs[0]
                      ? docs[0].url.startsWith('http')
                        ? docs[0].url
                        : `${apiBaseUrl}${docs[0].url}`
                      : null
                  }
                  bidSecurityUzs={data.bidSecurityUzs}
                  bidSecurityPct={data.bidSecurityPct}
                  budgetUzs={data.budgetUzs}
                  /* «Распознано со скана» — public, like the summary it sits
                     under. «оригиналом» jumps to the documents section under
                     the same condition that puts it in the section nav. */
                  textFromScan={!!data.textFromScan}
                  originalHref={docs.length > 0 || data.hasDocuments ? '#sec-documents' : null}
                />

                {/* «Пакет заявки». Rendered ONLY when the SERVER said so —
                    `bidPackage: true`, i.e. this account holds the
                    `bid_package` feature grant — and never re-derived from
                    auth-context, the same rule `unlocked` follows. Without the
                    grant the field is ABSENT (not false), the chunk is never
                    fetched, and this page is exactly what it was before the
                    feature existed. Placed right after the free understanding
                    of the lot and before the outcome: it is the next thing a
                    supplier does once they have decided to bid. */}
                {data.bidPackage && id ? (
                  /* `fallback={null}` and not a skeleton: the panel is a whole
                     card, and a placeholder card that turns into a real one
                     shifts everything under it. The chunk is small and the
                     button below it is what the reader came for. */
                  <Suspense fallback={null}>
                    <BidPackagePanel tenderId={id} />
                  </Suspense>
                ) : null}

                {/* Free content sections (single card; hidden when the tender has
                    no body yet, so we never paint an empty bordered box) */}
                {/* Separate self-guarding cards in the supplier's decision order:
                    Описание → Требования → Документация → Порядок (logistics last). */}

                {/* Award outcome. A `'failed'` lot («Закупка не состоялась»)
                    has no winner to test for, so it needs its OWN branch rather
                    than showing up as an ordinary expired lot with nothing here
                    — and it stays public. WHO won and FOR HOW MUCH is Pro since
                    2026-10-07: the API then sends `outcomeLocked` instead of the
                    three fields, and the wall takes this block's place. */}
                {data.outcomeLocked && data.outcomeStatus !== 'failed' ? (
                  <OutcomeWall
                    tenderId={id}
                    awardedAt={data.awardedAt ? formatDeadline(data.awardedAt, locale) : null}
                    flags={data}
                  />
                ) : data.winnerName || data.finalPriceUzs || data.outcomeStatus === 'failed' ? (
                  <div
                    className={`rounded-lg border border-edge p-5 shadow-m-sm sm:p-6 ${
                      data.outcomeStatus === 'failed' ? 'bg-bone-surface' : 'bg-status-active-bg/40'
                    }`}
                  >
                    <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
                      {t('tender.outcome.label')}
                    </h2>
                    {data.outcomeStatus === 'failed' ? (
                      <p className="text-[14px] font-semibold text-ink">
                        {t('tender.outcome.failed')}
                      </p>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {data.winnerName && (
                            <div>
                              <div className="text-[11px] text-ink-muted">
                                {t('tender.outcome.winner')}
                              </div>
                              {isLinkableTin(data.winnerInn) ? (
                                <Link
                                  to={`/supplier/${data.winnerInn}`}
                                  className="text-[14px] font-semibold text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand [overflow-wrap:anywhere]"
                                >
                                  {data.winnerName} · {data.winnerInn}
                                </Link>
                              ) : data.winnerInn ? (
                                // A non-9-digit id (e.g. `DSO-FZCO-35154`, `0000000`) is a real
                                // upstream identifier with no profile page — show it, don't link it.
                                <span className="text-[14px] font-semibold text-ink [overflow-wrap:anywhere]">
                                  {data.winnerName} · {data.winnerInn}
                                </span>
                              ) : (
                                <div className="text-[14px] font-semibold text-ink [overflow-wrap:anywhere]">
                                  {data.winnerName}
                                </div>
                              )}
                              {/* The winner's phone/email — PAID. The API sends
                                  `winnerContact` only to a full-access viewer, so
                                  for guests, free users and every prerendered
                                  page this renders nothing; no client-side gate
                                  is added (or needed) here. */}
                              <WinnerContactLines contact={data.winnerContact} className="mt-1" />
                              {data.winnerContactCapped && (
                                // Over today's ceiling of distinct lots (200,
                                // server-side): say so, softly, with the way to
                                // lift it, rather than let the line vanish and
                                // read as «no contact».
                                <WinnerContactCappedNote className="mt-1" />
                              )}
                            </div>
                          )}
                          {data.finalPriceUzs && (
                            <div>
                              <div className="text-[11px] text-ink-muted">
                                {t('tender.outcome.finalPrice')}
                              </div>
                              <div className="text-[14px] font-bold tabular-nums text-brand">
                                {formatBudget(data.finalPriceUzs)} {t('tender.budget.unit')}
                              </div>
                            </div>
                          )}
                        </div>
                        {data.awardedAt && (
                          <div className="mt-2 text-[12px] text-ink-muted">
                            {t('tender.outcome.awardedAt')}:{' '}
                            {formatDeadline(data.awardedAt, locale)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : null}

                {/* «Другие лоты этого объявления» — sibling lots of the same
                    KZ procurement announcement/batch (goszakup/mitwork/
                    skpharmacy routinely split one purchase into dozens of
                    single-product lots). Public, ungated. Self-hides — the API
                    only ever sends this when there are ≥2 siblings.

                    Redrawn 2026-10-15 after a phone pass on kaztender.com: the
                    header «Другие лоты этого объявления (12 лот(ов) · …)» did not
                    say what the list is FOR, and each row was one line — title
                    truncated to nothing beside a `shrink-0` amount, which a
                    35-digit numeric(65,30) budget pushed past the card edge. Now
                    the heading says it in a sentence («В этом же объявлении ещё
                    12 лотов на 4,2 млн ₸»), a hint says a bid can go to any of
                    them, and each row stacks title (two lines) over its amount,
                    so nothing can overflow at 320 px. */}
                {data.siblingLots && data.siblingLots.length > 0
                  ? (() => {
                      const siblingCount = data.siblingLotsTotal ?? data.siblingLots.length;
                      const siblingSum = formatBudgetCompact(
                        data.siblingLotsTotalBudgetUzs ?? null,
                        locale,
                      );
                      return (
                        <div
                          id="sec-siblings"
                          className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
                        >
                          <h2 className="text-[15px] font-bold leading-snug text-ink">
                            {siblingSum
                              ? t('tender.siblings.headline', {
                                  count: siblingCount,
                                  lots: lotsWord(siblingCount, locale),
                                  amount: `${siblingSum} ${t('tender.budget.unit')}`,
                                })
                              : t('tender.siblings.headlineNoSum', {
                                  count: siblingCount,
                                  lots: lotsWord(siblingCount, locale),
                                })}
                          </h2>
                          <p className="mb-3 mt-1 text-[13px] leading-snug text-ink-muted">
                            {t('tender.siblings.hint')}
                          </p>
                          <ul className="flex flex-col gap-2">
                            {data.siblingLots.map((sib) => (
                              <li key={sib.id}>
                                <Link
                                  to={tenderPath(sib.slug, sib.id)}
                                  className="block min-w-0 rounded-md border border-edge/70 bg-white px-3 py-2 transition-colors hover:border-brand/40"
                                >
                                  <span className="line-clamp-2 text-[13px] font-semibold leading-snug text-ink [overflow-wrap:anywhere]">
                                    {sib.title}
                                  </span>
                                  <span className="mt-0.5 block text-[12px] font-medium tabular-nums text-ink-muted">
                                    {sib.budgetUzs && formatBudget(sib.budgetUzs)
                                      ? `${formatBudget(sib.budgetUzs)} ${t('tender.budget.unit')}`
                                      : t(`tender.status.${statusKind(sib.status)}`)}
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                          {data.siblingLotsTotal != null &&
                            data.siblingLotsTotal > data.siblingLots.length && (
                              <p className="mt-2 text-[12px] text-ink-muted">
                                {t('tender.siblings.more', {
                                  count: data.siblingLotsTotal - data.siblingLots.length,
                                })}
                              </p>
                            )}
                        </div>
                      );
                    })()
                  : null}

                {/* Integrity facts — Pro-gated, self-hides when empty/anonymous.
                    Main column (not sidebar) so the re-listing signal leads. */}
                <TenderIntegrityPanel tenderId={id!} />

                {/* Описание / Спецификация / Требования now live in the single free
                    <TenderOverview> card above — folded together to kill the
                    three-way redundancy (summary vs source detail vs facts) and
                    ungated (understanding a tender is free). */}

                {/* Документация — colour-coded file rows (Pro; docs only present for
                    an entitled viewer). */}
                <div id="sec-documents" className="scroll-mt-16 empty:hidden">
                  {docs.length > 0 ? (
                    <DocumentsCard docs={docs} apiBase={apiBaseUrl} locale={locale} t={t} />
                  ) : data.documentsPreview && data.documentsPreview.length > 0 ? (
                    <LockedDocumentsCard
                      docs={data.documentsPreview}
                      locale={locale}
                      tenderId={id}
                      /* ИП: the padlock must lead to the open, not to /plans —
                         see the note on the component. `access` is the same
                         object the reveal card renders from. */
                      metered={
                        metered && access
                          ? {
                              opened: !!access.opened,
                              remaining: access.remaining ?? 0,
                              blocked: !!access.blockedByCeiling,
                            }
                          : null
                      }
                      sourceUrl={sourceUrl}
                      t={t}
                    />
                  ) : data.hasDocuments ? (
                    /* API one deploy behind (no `documentsPreview` yet) — the
                       old placeholder teaser, so the wall never disappears. */
                    <AnalyticsTeaser
                      wall="documents"
                      tenderId={id}
                      title={t('tender.documentsLabel')}
                      rows={[
                        t('tender.docsTeaser.tz'),
                        t('tender.docsTeaser.contract'),
                        t('tender.docsTeaser.spec'),
                      ]}
                    />
                  ) : null}
                </div>

                {/* Порядок проведения — FREE for everyone. It is procedural
                    public record (method, address, publication date, opening
                    time), not something we manufacture, and it helps a reader
                    judge whether the tender is worth pursuing at all. The paid
                    value stays contacts / documents / full summary / analytics.
                    The API already ships `procedure` ungated, so this is purely
                    a rendering gate. Kept in sync with the plans page, which no
                    longer sells it as a Pro feature. */}
                {/* Public per-lot supply detail (goszakup). Ungated — part of
                    understanding the lot, like the description/spec above it. */}
                {lotRows.length > 0 || lotPos.positions.length > 0 ? (
                  <div
                    id="sec-lot-details"
                    className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
                  >
                    <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
                      {t('tender.lotDetails.title')}
                    </h2>
                    <dl className="grid gap-2.5 text-[14px]">
                      {lotRows.map(([key, value]) => (
                        <div key={key} className="flex gap-2">
                          <dt className="w-40 shrink-0 text-ink-muted">
                            {t(`tender.lotDetails.${key}`)}
                          </dt>
                          <dd className="font-medium text-ink">{value}</dd>
                        </div>
                      ))}
                    </dl>
                    {lotPos.positions.length > 0 && (
                      <div className={lotRows.length > 0 ? 'mt-4' : ''}>
                        <h3 className="mb-1.5 text-[13px] font-semibold text-ink-muted">
                          {t('tender.lotDetails.positions', { n: lotPos.total })}
                        </h3>
                        <ol className="grid gap-2 text-[14px]">
                          {lotPos.positions.map((p, i) => (
                            <li
                              key={i}
                              className="border-t border-edge-light pt-2 first:border-t-0"
                            >
                              <div className="font-medium text-ink">
                                {p.name ?? `#${i + 1}`}
                                {p.code && (
                                  <span className="ml-1.5 text-[12px] font-normal text-ink-muted">
                                    {p.code}
                                  </span>
                                )}
                              </div>
                              {positionLine(p, locale) && (
                                <div className="text-[13px] tabular-nums text-ink-muted">
                                  {positionLine(p, locale)}
                                </div>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                ) : null}

                {procRows.length > 0 ? (
                  <div
                    id="sec-procedure"
                    className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
                  >
                    <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
                      {t('tender.procedureLabel')}
                      {locale === 'uz' && (
                        <span className="ml-2 font-medium normal-case tracking-normal text-ink-muted/80">
                          ({t('tender.sourceLangNote')})
                        </span>
                      )}
                    </h2>
                    <dl className="grid gap-2.5 text-[14px]">
                      {procRows.map(([key, value]) => (
                        <div key={key} className="flex gap-2">
                          <dt className="w-40 shrink-0 text-ink-muted">
                            {key === 'positions' ? (
                              <span className="inline-flex items-center gap-1">
                                {t('tender.procedure.positions')}
                                <InfoHint text={t('tender.procedure.positionsHint')} />
                              </span>
                            ) : (
                              t(`tender.procedure.${key}`)
                            )}
                          </dt>
                          <dd className="font-medium text-ink">{value}</dd>
                        </div>
                      ))}
                    </dl>
                    {/* Newcomer pointer to the supplier guide (registration,
                        E-IMZO, submission) — its natural home, beside the
                        procedure. Shows only where a tender has procedure rows. */}
                    <Link
                      to="/guide"
                      className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-brand transition-colors hover:text-brand-mid"
                    >
                      {t('tender.participateHint')}
                      <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                    </Link>
                  </div>
                ) : null}

                {/* Soliq registry trust facts for the buyer org — «О заказчике».
                    Public data (the org's own registry state), free like the
                    rest of "understanding" the tender; sits right above the
                    contact section it's meant to inform. `buyerTrust` is null
                    when the tender has no customer TIN or no matching org row,
                    and the component itself renders nothing when every field
                    it got is empty (soliq negative-cache row). */}
                {/* The buyer→profile link now lives INSIDE the trust card
                    (its footer) — the ONE contextually-relevant link out of a
                    tender, and the crawl path that ends the buyer pages' orphan
                    status (only for a 9-digit ИНН with a real profile page).
                    When the buyer has no soliq facts the card renders nothing
                    but the link still shows on its own. */}
                {(data.buyerTrust || isLinkableTin(data.buyerTin)) && (
                  <BuyerTrustStrip
                    {...(data.buyerTrust ?? {})}
                    profileLink={
                      isLinkableTin(data.buyerTin) ? (
                        /* 2026-09-06: the TRANSITION is Pro-only, the page is not.
                           The href stays the real /buyer/:tin — it is the crawl
                           path that keeps the buyer pages out of orphan status,
                           and the profile page itself is public for SEO with its
                           own wall. A non-Pro human who clicks is sent to /plans
                           instead; the lock glyph says so before the click.
                           `proUnlocked`, not `unlocked`: a metered viewer's
                           spent open buys this lot's contact and source link,
                           not the buyer's award graph, and the profile page
                           would wall them after the click anyway. */
                        <Link
                          to={`/buyer/${data.buyerTin}`}
                          onClick={(e) => {
                            if (proUnlocked) return;
                            e.preventDefault();
                            track('upgrade_cta_clicked', { source: 'buyerProfileLink' });
                            void navigate(localePath(plansPathWithContext('paywall', id), locale));
                          }}
                          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-brand transition-colors hover:text-brand-mid [overflow-wrap:anywhere]"
                        >
                          {!proUnlocked && <span aria-hidden="true">🔒</span>}
                          {t('tender.buyer.profileLink')}
                        </Link>
                      ) : undefined
                    }
                  />
                )}

                {/* Pro wall — the inline gate (guest → register; free → hard Pro
                    wall). `.tender-paid` targets the JSON-LD paywall markup. A Pro
                    viewer is `unlocked` and sees the content instead; a tender with
                    nothing behind the wall (`!hasGatedContent`) shows no wall at all. */}
                {showReveal ? (
                  <div id="sec-reveal" className="tender-paid scroll-mt-4">
                    <ContactRevealCard
                      tenderId={id!}
                      access={access!}
                      hasContact={data.hasContact !== false}
                    />
                  </div>
                ) : (
                  showGate && (
                    <div className="tender-paid">
                      {/* ONE card: the locked rows + the free-tier monthly-unlock
                          action (+ «оформить Pro»). `freeUnlock` is only populated
                          for an authed non-Pro viewer (see catalog.service.ts), so
                          for a guest the card falls back to the register/Pro CTA. */}
                      <TenderGateCard
                        state={gateState}
                        freeUnlock={data.freeUnlock}
                        onUnlock={handleFreeUnlock}
                        // Kazakhstan only (`honestTeasers`): list what the
                        // lot has. Absent props = both rows, as on main.
                        {...(countryConfig.features.honestTeasers
                          ? {
                              hasDocuments: !!data.hasDocuments,
                              hasContact: data.hasContact !== false,
                            }
                          : {})}
                      />
                    </div>
                  )
                )}
              </main>

              {/* Sticky rail (desktop) — budget/deadline, the primary CTA, the
                  source link, and the compact PRO analytics cards. On mobile the
                  aside stacks after the content and shows analytics only (the
                  facts + CTA already sit under the title above). */}
              <aside className="mt-5 flex flex-col gap-4 lg:mt-0 lg:sticky lg:top-5 lg:self-start">
                <div className="hidden lg:flex lg:flex-col lg:gap-3">
                  <RailFacts
                    budgetUzs={data.budgetUzs}
                    locale={locale}
                    deadline={deadline}
                    daysLeftN={dLeft}
                    kind={kind}
                    hideBudget={isSupply}
                    announcement={announcement}
                    bidsOpenOn={bidsOpenOn}
                  />
                  {announcement && <AnnouncementNote />}
                  {singleSupplier && <SingleSupplierNote />}
                  {/* Same order as the mobile block: «Открыть оригинал» first,
                      the waitlist button right under it, same width. */}
                  <OriginalCta
                    unlocked={unlocked}
                    sourceUrl={sourceUrl}
                    supply={isSupply}
                    tenderId={id}
                    metered={metered}
                  />
                  {BID_WAITLIST_VISIBLE && id && (
                    <BidWaitlistButton
                      tenderId={id}
                      granted={!!data.bidPackage}
                      closed={kind === 'closed'}
                      deadline={data.deadline}
                    />
                  )}
                </div>
                {/* КОНТАКТНОЕ ЛИЦО — the inline contact (Pro), in the rail (desktop).
                    Ships in the payload for a Pro viewer; null otherwise. */}
                {contact && (contact.name || contact.phone || contact.email) && (
                  <div
                    id="sec-contacts"
                    className="ph-mask scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm"
                  >
                    <div>
                      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
                        {t('tender.contactName')}
                      </div>
                      {contactOrg && (
                        <div className="text-[15px] font-bold text-ink [overflow-wrap:anywhere]">
                          {contactOrg}
                        </div>
                      )}
                      {contactInn && (
                        <div className="mt-0.5 text-[13px] text-ink-muted">
                          {t('org.tinNumber', { number: contactInn })}
                        </div>
                      )}
                      {contact.phone && (
                        <a
                          href={`tel:${contact.phone}`}
                          className="mt-2 block text-[13px] font-medium text-brand hover:underline"
                        >
                          {formatPhone(contact.phone)}
                        </a>
                      )}
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="block text-[13px] font-medium text-brand hover:underline [overflow-wrap:anywhere]"
                        >
                          {contact.email}
                        </a>
                      )}
                    </div>
                  </div>
                )}
                <BaseRatesPanel tenderId={id!} />
                <BuyerGraphPanel tenderId={id!} />
                <SimilarWinnersPanel tenderId={id!} />
              </aside>
            </div>

            {/* Full width, BELOW the grid — deliberately not in the <aside>.
                The sidebar is where the Pro-gated panels live, and those render
                nothing for anonymous viewers; this block must render for
                everyone (and for Googlebot) or it does not do its job. */}
            {!hideOldSimilar && <SimilarTenders tenderId={id!} locale={locale} />}
            <ChannelSubscribeCta surface="tenderDetail" />

            {/* The quiet opt-out / correction link (owner, 2026-10-07) — only
                where a winner's contact is (or would be) shown to a paying
                reader. Igor, 10.10: it sat right under the phone, where
                ordinary buyers pressed it; now it is the page's last line,
                small and set apart. It opens the support form as a modal
                (`ContactSupport`, topic `company_data`) — no page of its own. */}
            {(data.winnerContact || data.winnerContactCapped) && isLinkableTin(data.winnerInn) && (
              <CompanyDataLink tin={data.winnerInn as string} className="mt-10 pb-2" />
            )}

            {/* Sticky mobile bottom action bar (below `lg`). Non-Pro converts
                (register / Pro); Pro gets one-tap contacts (call the inline
                phone, else jump to the КОНТАКТНОЕ ЛИЦО card). The BAR ITSELF is
                conditional: when there is no action to offer — a non-Pro viewer
                on a tender with nothing behind the wall, or a Pro viewer with no
                contact — it used to render as an empty sticky strip. */}
            {/* `contact` covers the metered viewer too: once they spend an open
                the contact ships inline, so the call/jump action is right for
                them. `showGate` is already false while metered, so no wall
                competes with the reveal card.
                ⚠️ `hasRealContact`, not `contact`: an open on a lot whose
                заказчик published nothing returns a contact OBJECT with three
                nulls, which is truthy. That drew the bar with a «Контакты»
                anchor jumping to a #sec-contacts card that never rendered.
                ⚠️ `showReveal` is load-bearing, and its absence was a REPORTED
                DEFECT (ИП subscriber, 2026-09-11 05:17 UTC): a metered viewer
                who has not yet spent an open has `showGate === false` (the wall
                is exclusive while metered), `unlocked === false` and no
                `contact` yet — so the whole condition collapsed and the phone
                got NO bar at all, while a FREE viewer of the same lot gets one.
                `OriginalCta` also returns null for them, so the only control
                left was the reveal card at the very bottom of the page, below
                описание / спецификация / требования / документы / рыночные
                данные. The paying tier had a worse path to its own paid action
                than the free tier. */}
            {(showGate || showReveal || ((unlocked || metered) && hasRealContact)) && (
              <div
                // `data-sticky-cta` lets the shell's scroller pad itself by the
                // bar's height (globals.css), so the footer's last lines scroll
                // clear of it instead of ending underneath.
                data-sticky-cta=""
                className="fixed inset-x-0 bottom-0 z-30 border-t border-edge bg-bone-surface/95 px-4 py-3 backdrop-blur lg:hidden"
                style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
              >
                <div className="mx-auto max-w-3xl">
                  {showReveal ? (
                    /* Jump to the card rather than duplicating its button: the
                       reveal owns pending/error/ceiling state, and a second copy
                       of a control that SPENDS AN ALLOWANCE is exactly the thing
                       that must not drift out of sync with the server's rules.
                       The label matches the card's own CTA, so the bar promises
                       the same action the user lands on. */
                    <a
                      href="#sec-reveal"
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById('sec-reveal')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] text-[14px] font-bold text-white transition-colors hover:bg-brand-mid"
                    >
                      {t(
                        data.hasContact !== false
                          ? 'tender.reveal.action'
                          : 'tender.reveal.actionNoContact',
                      )}
                    </a>
                  ) : !unlocked && !hasRealContact ? (
                    <PrimaryCta gateState={gateState} unlocked={unlocked} tenderId={id} />
                  ) : contact?.phone ? (
                    <a
                      href={`tel:${contact.phone}`}
                      className="ph-mask inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] text-[14px] font-bold text-white transition-colors hover:bg-brand-mid"
                    >
                      <PhoneIcon aria-hidden className="h-[18px] w-[18px]" />
                      {t('tender.callAction')} {formatPhone(contact.phone)}
                    </a>
                  ) : (
                    <a
                      href="#sec-contacts"
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .getElementById('sec-contacts')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] text-[14px] font-bold text-white transition-colors hover:bg-brand-mid"
                    >
                      {t('tender.nav.contacts')}
                    </a>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
